import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import Button from "components/common/Button";
import GoogleAddress from "components/common/GoogleAddress";
import Input from "components/common/Input";
import api from "config/api";
import { addMinutes, format, subMinutes } from "date-fns";
import { useAppDispatch, useAppSelector } from "hooks/store";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  setGestData,
  setIsGuestUser,
  setUserData,
  updateCheckoutData,
} from "store/slices/main.slice";
import { convertToTimezone } from "utils";
import { ORDER, ORDER_NAME, TIME_ZONES } from "utils/constants";

type Props = {
  setStage: (v: string) => void;
};

const formatGlobalTime = (timeZone: string) => {
  const formattedTime = convertToTimezone(new Date(), TIME_ZONES[timeZone]);
  console.log("🚀 - useEffect - formattedTime:", formattedTime);

  if (!formattedTime) {
    throw new Error("Formatted time is undefined or invalid.");
  }

  const [date, time] = formattedTime.split(", ");
  const [mon, day, year] = date.split("/");
  let [hr, min, sec]: string[] | number[] = time.split(":");

  const isPM = time.includes("PM");
  hr = isPM ? (hr === "12" ? 12 : +hr + 12) : hr === "12" ? 0 : +hr;
  sec = sec ? sec.replace(/ AM| PM/, "") : "0";

  const currentTime = new Date();
  currentTime.setMonth(+mon - 1);
  currentTime.setDate(+day);
  currentTime.setFullYear(+year);
  currentTime.setHours(+hr, +min, +sec);
  return currentTime;
};

const initialData = {
  address: "",
  tip: 0,
  clientId: 0,
  suitNo: "",
  buzzerNo: "",
  roomNo: "",
  firstName: "",
  lastName: "",
  address1: "",
  phoneNo: "",
  email: "",
  password: "",
  confirmPassword: "",
  onDate: format(new Date(), "yyyy-MM-dd"),
  onTime: format(new Date(), "HH:mm"),
  selectedAddress: "home",
};

const orderLable: any = {
  TakeOut: ORDER_NAME.TakeOut,
  Delivery: ORDER_NAME.Delivery,
};

const Details = ({ setStage }: Props) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [data, setData] = useState(initialData);
  const [tab, setTab] = useState("Login");
  const [isDisableButtons, setIsDisableButtons] = useState<{
    Delivery: boolean;
    TakeOut: boolean;
    Both: boolean;
  }>({ Delivery: false, TakeOut: false, Both: false });
  const [isFetchingRestDates, setIsFetchingRestDates] = useState(false);

  const {
    userData,
    checkoutData,
    validTimes,
    settings,
    companyData,
    gestData,
  } = useAppSelector(({ main }) => main);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Helper: Always prefer logged-in user details. Only fallback to guest details if no userData.
  const preferredProfileData = useMemo(() => {
    return userData ? userData : gestData ? gestData : null;
  }, [userData, gestData]);

  // Helper: Safe display value for labels (prevents showing "undefined")
  const dcsSafe = (v: any) => (v === null || v === undefined ? "" : String(v));
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-06 ===
  // Premium, single-border input styling + consistent label size (fixes "too big label" + removes double borders)
  // + Center Continue button on any device
  // + FIX: Google Places dropdown not visible inside modal (z-index + style for .pac-container)
  const UI_POLISH_CSS = `
    .dcs-details-ui{
      background: #fff;
    }

    /* Card feel */
    .dcs-details-card{
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0 10px 28px rgba(164, 168, 177, 0.25);
      border: 1px solid rgba(16,24,40,.06);
      overflow: hidden;
    }

    /* Normalize label size (Email looked OK, Password looked too big) */
    .dcs-details-ui .MuiFormLabel-root,
    .dcs-details-ui .MuiInputLabel-root{
      font-size: 12px !important;
      line-height: 1.2 !important;
      letter-spacing: .2px;
    }

    /* Single smooth outline (prevents “double/triple border” look) */
    .dcs-details-ui .MuiOutlinedInput-root{
      border-radius: 12px !important;
      background: #fff;
    }
    .dcs-details-ui .MuiOutlinedInput-notchedOutline{
      border-width: 1px !important;
      border-color: rgba(16,24,40,.16) !important;
    }

    /* Soft focus glow (premium look) */
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline{
      border-color: rgba(25,118,210,.45) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused{
      box-shadow: 0 0 0 4px rgba(25,118,210,.12);
    }

    /* Remove any extra border effects from hover */
    .dcs-details-ui .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline{
      border-color: rgba(16,24,40,.28) !important;
    }

    /* Make Radio label text match premium scale */
    .dcs-details-ui .MuiFormControlLabel-label{
      font-size: 13px;
    }

    /* Continue button centered on any device */
    .dcs-continue-wrap{
      display: flex;
      justify-content: center;
      width: 100%;
      padding: 0 16px;
      margin-top: 6px;
    }
    .dcs-continue-btn{
      width: 100%;
      max-width: 420px; /* keeps it centered + premium on desktop, full-width on mobile */
    }

    /* === ADDED BY SAMPATH – 2026-01-06 ===
       FIX: Google Places suggestions dropdown hidden behind MUI Dialog/backdrop.
       Places dropdown uses .pac-container appended to <body>, so we force high z-index.
    */
    .pac-container{
      z-index: 20000 !important;
      border-radius: 12px !important;
      overflow: hidden !important;
      border: 1px solid rgba(16,24,40,.12) !important;
      box-shadow: 0 14px 30px rgba(16,24,40,.16) !important;
      font-family: inherit !important;
    }
    .pac-item{
      padding: 10px 12px !important;
      cursor: pointer !important;
    }
    .pac-item:hover{
      background: rgba(16,24,40,.04) !important;
    }
    .pac-item, .pac-item span{
      font-size: 13px !important;
      line-height: 1.25 !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       HEADER: Login LEFT, Register + Guest RIGHT (modern look).
       Selected tab = bold black + underline.
    */
    .dcs-auth-top{
      display:flex;
      align-items:center;
      justify-content:space-between;
      padding: 10px 0px 6px 0px;
    }

    /* Modern subtle "pill" background (no harsh lines) */
    .dcs-auth-shell{
      background: rgba(16,24,40,.04);
      border-radius: 9999px;
      padding: 6px 10px;
    }

    .dcs-auth-left{
      font-size: 13px;
      font-weight: 600;
      color: rgba(16,24,40,.55);
      cursor: pointer;
      user-select:none;
      border-bottom: 2px solid transparent;
      padding: 6px 10px 4px 10px;
      border-radius: 9999px;
    }
    .dcs-auth-right{
      display:flex;
      gap: 10px;
      align-items:center;
    }
    .dcs-auth-link{
      font-size: 13px;
      font-weight: 600;
      color: rgba(16,24,40,.55);
      cursor: pointer;
      user-select:none;
      border-bottom: 2px solid transparent;
      padding: 6px 10px 4px 10px;
      border-radius: 9999px;
    }
    .dcs-auth-active{
      color: #101828 !important;
      font-weight: 800 !important;
      border-bottom-color: #101828 !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       REMOVE EVERY GRID/SECTION BORDER (no header lines, no dividers, no card border, no "ring" outlines).
       (Input borders stay as-is.)
    */
    .dcs-details-card{
      border: 0 !important;
    }
    .dcs-details-ui .border-b{
      border-bottom: 0 !important;
    }
    .dcs-details-ui .border{
      border: 0 !important;
    }
    .dcs-details-ui .ring-1{
      box-shadow: none !important; /* tailwind ring uses box-shadow */
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       TEXT FIELD STYLE like your screenshot:
       - light gray field background
       - softer border (almost invisible)
       - consistent height + padding
       - nice focus ring using theme primary color
    */
    .dcs-details-ui .MuiOutlinedInput-root{
      background: rgba(16,24,40,.04) !important;
      border-radius: 14px !important;
    }
    .dcs-details-ui .MuiOutlinedInput-notchedOutline{
      border-color: rgba(16,24,40,.10) !important;
    }
    .dcs-details-ui .MuiInputBase-input{
      padding: 12px 14px !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
    }
    .dcs-details-ui .MuiInputLabel-root{
      color: rgba(16,24,40,.55) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused{
      box-shadow: 0 0 0 4px rgba(0,0,0,.06) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline{
      border-color: rgba(0,0,0,.25) !important;
    }

    /* Order type bar bottom margin 10px */
    .dcs-order-type-bar{
      margin-bottom: 10px !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       MASTER DETAILS (Label view, NOT input fields)
       Matches theme-like soft transparency, clean labels.
    */
    .dcs-master-card{
      background: rgba(16,24,40,.035);
      border: 1px solid rgba(16,24,40,.06);
      border-radius: 14px;
      padding: 12px 12px;
    }
    .dcs-master-row{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 14px;
      padding: 8px 2px;
    }
    .dcs-master-row + .dcs-master-row{
      border-top: 1px solid rgba(16,24,40,.06);
    }
    .dcs-master-label{
      font-size: 12px;
      color: rgba(16,24,40,.55);
      font-weight: 600;
      white-space: nowrap;
      padding-top: 2px;
    }
    .dcs-master-value{
      font-size: 14px;
      color: #101828;
      font-weight: 500;
      text-align: right;
      word-break: break-word;
      max-width: 70%;
    }
    /* === END SAMPATH === */
  `;
  // === END SAMPATH ===

  useEffect(() => {
    let updatedData = {
      ...initialData,
      onTime: format(formatGlobalTime(companyData?.timeZone!), "HH:mm"),
    };
    const currentTime = format(formatGlobalTime(companyData?.timeZone!), "HH:mm");
    const currentTimeObj = formatGlobalTime(companyData?.timeZone!);

    if (moment(currentTimeObj).isValid()) {
      updatedData.onDate = format(currentTimeObj, "yyyy-MM-dd");
    }

    if (!data.clientId) {
      // === ADDED BY SAMPATH – 2026-01-15 ===
      // Always load logged-in user details first. If no userData, then load guest details.
      if (preferredProfileData) {
        updatedData = { ...updatedData, ...preferredProfileData };
      }
      // === END SAMPATH ===

      // (kept original selectedAddress logic)
      if (checkoutData.selectedAddress) {
        updatedData.selectedAddress = checkoutData.selectedAddress;
      }

      if (
        format(currentTimeObj, "yyyy-MM-dd") === updatedData.onDate ||
        format(currentTimeObj, "yyyy-MM-dd") > updatedData.onDate
      ) {
        const [hours, minutes] = currentTime.split(":").map(Number);
        currentTimeObj.setHours(hours, minutes);

        let updatedTime: Date | undefined;

        if (companyData && checkoutData.orderType === "Delivery") {
          updatedTime = addMinutes(currentTimeObj, companyData.deliveryTime);
        } else if (companyData && checkoutData.orderType === "TakeOut") {
          updatedTime = addMinutes(currentTimeObj, companyData.pickUpTime);
        }

        if (updatedTime) {
          const selectedTime: Date = currentTimeObj;
          const [selectedHours, selectedMinutes] = updatedData.onTime
            .split(":")
            .map(Number);
          selectedTime.setHours(selectedHours, selectedMinutes);

          if (selectedTime < updatedTime) {
            updatedData.onTime = format(updatedTime as Date, "HH:mm");
          }
        }
      }
      setData(updatedData);
    }
  }, [
    companyData,
    checkoutData,
    gestData,
    userData,
    tab,
    data.clientId,
    // === ADDED BY SAMPATH – 2026-01-15 ===
    preferredProfileData,
    // === END SAMPATH ===
  ]);

  const loginUser = (params: any) => {
    const { onDate, onTime } = data;
    setLoading(true);
    api(`/api/Customer/ByIdPass?${new URLSearchParams(params)}`)
      .then(({ data }) => {
        setData((e) => ({ ...e, ...data }));
        dispatch(setUserData(data));

        // === ADDED BY SAMPATH – 2026-01-15 ===
        // After login, force non-guest mode so UI always prefers userData
        dispatch(setIsGuestUser(false));
        setTab("Login");
        // === END SAMPATH ===

        toast.success("Login Successfully");
        setStage("DETAILS");
        dispatch(updateCheckoutData({ ...data, onDate, onTime }));
      })
      .catch(() => {
        toast.error("Invalid Email or Password");
      })
      .finally(() => setLoading(false));
  };

  const registerUser = (params: any) => {
    const { onDate, onTime } = data;
    setLoading(true);
    api
      .post(`/api/Customer`, params)
      .then(({ data }) => {
        dispatch(setUserData(data));
        setStage("DETAILS");
        setData({ ...data, onDate, onTime });

        // === ADDED BY SAMPATH – 2026-01-15 ===
        // After register, force non-guest mode so UI always prefers userData
        dispatch(setIsGuestUser(false));
        setTab("Login");
        // === END SAMPATH ===

        dispatch(updateCheckoutData({ ...data, onDate, onTime }));
        toast.success("User created Successfully");
      })
      .catch((e) => {
        toast.error(e || "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async () => {
    toast.dismiss();

    let isTimeValid = true;
    let { onDate, onTime } = data;

    if (userData || (data && data.clientId)) {
      if (format(new Date(), "yyyy-MM-dd") === data.onDate) {
        let currentTime: Date = new Date();

        const formattedTime = convertToTimezone(
          new Date(),
          TIME_ZONES[companyData?.timeZone!]
        );

        const [date, time] = formattedTime.split(", ");

        const [mon, _, year] = date.split("/");
        console.log("\n _ ----->", _);
        const [hr, min, sec] = time.replace(" AM", "").split(":");

        currentTime.setMonth(+mon - 1);
        currentTime.setFullYear(+year);
        currentTime.setHours(+hr, +min, +sec);

        const formattedTimeInHour = moment().format("HH:mm");

        if (formattedTimeInHour && formattedTimeInHour.includes(":")) {
          const [hours, minutes] = formattedTime.split(":").map(Number);

          if (!isNaN(hours) && !isNaN(minutes)) {
            currentTime.setHours(hours, minutes);
          }
        }

        let updatedTime: Date | undefined;

        if (companyData && checkoutData.orderType === "Delivery") {
          updatedTime = addMinutes(currentTime, companyData.deliveryTime);
        } else if (companyData && checkoutData.orderType === "TakeOut") {
          updatedTime = addMinutes(currentTime, companyData.pickUpTime);
        }

        if (updatedTime) {
          const selectedTime: Date = new Date();

          const [selectedHours, selectedMinutes] = data.onTime
            .split(":")
            .map(Number);

          selectedTime.setHours(selectedHours, selectedMinutes);

          console.log("selectedTime", selectedHours);

          if (selectedTime < updatedTime) {
            setData({ ...data, onTime: format(updatedTime, "HH:mm") });
            onTime = format(updatedTime, "HH:mm");
            toast.success(
              `Your ${
                checkoutData.orderType === "Delivery" ? "delivery" : "takeout"
              } time has been updated to ${format(updatedTime, "HH:mm")}.`
            );

            isTimeValid = true;
          }
        }
      }

      if (isTimeValid) {
        if (checkoutData.orderType === ORDER.DELIVERY) {
          if (!data?.address && !data?.address1) {
            toast.error("At least one address is required.");
            return;
          }

          if (!data?.selectedAddress) {
            toast.error("Please select a delivery address.");
            return;
          }

          if (data.selectedAddress === "home" && !data?.address) {
            toast.error("Home address is required.");
            return;
          }

          if (data.selectedAddress === "office" && !data?.address1) {
            toast.error("Office address is required.");
            return;
          }
          if (data.selectedAddress === "home" && data?.address) {
            data.address = data?.address || userData?.address;
          } else if (data.selectedAddress === "office" && data?.address1) {
            data.address1 = data?.address1 || userData?.address1;
          }
        }
        if (userData) {
          const currentUserData = {
            ...userData,
            address: data?.address || "",
            ...(data?.address1 || userData?.address1
              ? { address1: data.address1 || userData.address1 }
              : {}),
          };

          const isAddressSame =
            data?.address === userData?.address &&
            ((data?.address1 &&
              userData?.address1 &&
              data.address1 === userData.address1) ||
              (!data?.address1 && !userData?.address1));

          if (!isAddressSame) {
            api
              .post("/api/Customer/UpdateCustomerData", currentUserData)
              .then(() => {
                toast.success("User details updated successfully");
                dispatch(setUserData(structuredClone(currentUserData)));
              })
              .catch(() => {
                toast.error("Something went wrong while updating user details");
              });
          }
        }

        setStage("CHECKOUT");
        dispatch(updateCheckoutData(data));
        return;
      }
    }

    if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        data.email
      )
    ) {
      toast.error("Invalid Email");
      return;
    }

    setTimeout(() => {
      if (tab === "Login") {
        if (data.password.length <= 3) {
          toast.error("Password must be greater than 3 characters");
          return;
        }
        loginUser({
          emailId: data.email,
          password: data.password,
        });
        return;
      }

      if (!data.firstName || !data.lastName) {
        toast.error("First Name & Last Name are required");
        return;
      }

      if (!/^\d{10}$/.test(data.phoneNo)) {
        toast.error("Invalid Phone Number");
        return;
      }

      if (checkoutData.orderType === ORDER.DELIVERY) {
        if (!data?.address) {
          toast.error("Address is Required");
          return;
        }
      }

      if (tab === "Register") {
        if (data.password.length <= 3) {
          toast.error("Password must be greater than 3 characters");
          return;
        }
        if (data.password !== data.confirmPassword) {
          toast.error("Confirm password does not match");
          return;
        }
        registerUser(data);
        return;
      }

      if (tab === "Guest") {
        setLoading(true);
        api
          .get(`/api/Customer/ByPhone?phoneNo=${data.phoneNo}`)
          .then(({ data }) => {
            setStage("DETAILS");
            delete data.address;
            delete data.address1;
            dispatch(setGestData({ ...data, onDate, onTime }));
            setData((e) => ({ ...e, ...data, onDate, onTime }));
            toast.success("Guest Verified Successfully");
            setLoading(false);
          })
          .catch(() => {
            api
              .post(`api/Customer`, { ...data, password: "123" })
              .then(({ data }) => {
                setStage("DETAILS");
                delete data.address;
                delete data.address1;
                setData((e) => ({ ...e, ...data, onDate, onTime }));
                dispatch(setGestData({ ...data, onDate, onTime }));
                toast.success("Guest Verified Successfully");
              })
              .catch(() => toast.error("Something went wrong"))
              .finally(() => setLoading(false));
          });
      }
    }, 300);
  };

  const checkTimeRange = (
    from: Date | undefined,
    to: Date | undefined,
    hour: number,
    min: number
  ) => {
    let error = false;
    if (!from || !to) return false;
    if (
      hour < from.getHours() ||
      (hour > to.getHours() && from.getDate() === to.getDate())
    ) {
      error = true;
    }

    if (hour === from.getHours() && min < from.getMinutes()) {
      error = true;
    }

    if (hour === to.getHours() && min > to.getMinutes()) {
      error = true;
    }
    return !error;
  };

  const timeError = useMemo(() => {
    if (!userData) return "";

    const [hour, min] = data.onTime.split(":");
    const day = format(new Date(data.onDate), "EEEE");

    const found = validTimes.find(
      (i: any) => i.timDay === day && i.timOrdtype === checkoutData.orderType
    );

    if (!found || found?.timIs24Hr) return "";
    const extraTime =
      (checkoutData.orderType === ORDER.DELIVERY
        ? companyData?.deliveryTime
        : companyData?.pickUpTime) || 0;

    const from = addMinutes(new Date(found.timFrom), extraTime);
    const to = subMinutes(new Date(found.timTo), extraTime);
    const from1 = found.timFrom1
      ? addMinutes(new Date(found.timFrom1), extraTime)
      : undefined;
    const to1 = found.timTo1
      ? subMinutes(new Date(found.timTo1), extraTime)
      : undefined;
    const from2 = found.timFrom2
      ? addMinutes(new Date(found.timFrom2), extraTime)
      : undefined;
    const to2 = found.timTo2
      ? subMinutes(new Date(found.timTo2), extraTime)
      : undefined;

    let error = `${
      ORDER_NAME[checkoutData.orderType]
    } is closed at the moment, you can make this order between `;

    if (from && to) {
      error += `${format(from, "hh:mm aa")} - ${format(to, "hh:mm aa")}`;
    }
    if (from1 && to1) {
      error += `, ${format(from1, "hh:mm aa")} - ${format(to1, "hh:mm aa")}`;
    }
    if (from2 && to2) {
      error += `, ${format(from2, "hh:mm aa")} - ${format(to2, "hh:mm aa")}`;
    }

    error += ` on ${day}`;
    const t = checkTimeRange(from, to, +hour, +min);
    const t1 = checkTimeRange(from1, to1, +hour, +min);
    const t2 = checkTimeRange(from2, to2, +hour, +min);

    if (t || t1 || t2) {
      return "";
    }
    return error;
  }, [
    validTimes,
    data.onTime,
    data.onDate,
    checkoutData.orderType,
    companyData,
    userData,
  ]);

  const isRestDate = useMemo(
    () =>
      (checkoutData.orderType === ORDER.TAKE_OUT && isDisableButtons.TakeOut) ||
      (checkoutData.orderType === ORDER.DELIVERY &&
        isDisableButtons.Delivery) ||
      isDisableButtons.Both,
    [isDisableButtons, checkoutData.orderType]
  );

  const fetchData = () => {
    setIsFetchingRestDates(true);
    api
      .get("api/RestDates/GetRestdates")
      .then(({ data: restDatesData }) => {
        function checkOrderAvailability(
          orderType: "TakeOut" | "Delivery" | "Both"
        ) {
          const currentDate = new Date(data.onDate);

          const filteredOrders = restDatesData.filter(
            (item: any) => item.timdOrdtype === orderType
          );

          for (let order of filteredOrders) {
            const fromDate = new Date(order.timdFrom);
            const toDate = new Date(order.timdTo);

            if (currentDate >= fromDate && currentDate <= toDate) {
              return true;
            }
          }

          return false;
        }

        setIsDisableButtons({
          Delivery: checkOrderAvailability("Delivery"),
          TakeOut: checkOrderAvailability("TakeOut"),
          Both: checkOrderAvailability("Both"),
        });
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setTimeout(() => {
          setIsFetchingRestDates(false);
        }, 1000);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.onDate]);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // IMPORTANT FIX:
  // Do NOT let guest data overwrite logged-in user data.
  // Only apply gestData to local form when NO userData and the current tab is Guest.
  useEffect(() => {
    if (!userData && tab === "Guest" && gestData) {
      setData(gestData);
    }
  }, [gestData, userData, tab]);
  // === END SAMPATH ===

  if (!checkoutData) return <></>;

  return isFetchingRestDates ? (
    <div className="flex justify-center items-center w-full h-auto ">
      <div className="w-10 h-10  border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
    </div>
  ) : (
    <>
      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      <style>{UI_POLISH_CSS}</style>
      {/* === END SAMPATH === */}

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      <div className="dcs-details-ui p-3 sm:p-4">
        <div className="dcs-details-card max-w-[720px] mx-auto">
          {/* === END SAMPATH === */}

          <div className="flex justify-between items-center px-4 pb-2 border-b">
            <div
              onClick={() => {
                if (tab === "Guest" && data.clientId) {
                  setData((e) => ({ ...e, clientId: 0 }));
                } else {
                  setStage("CART");
                }
              }}
            >
              <ArrowBackIosNewIcon className="!text-[15px] !text-primary cursor-pointer" />
            </div>
            <h4 className="!text-lg text-primary font-semibold text-center">
              Profile Details
            </h4>
            <div />
          </div>

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* === ADDED BY SAMPATH – 2026-01-15 === */}
          {/* Order Type bottom margin 10px */}
          <div className="dcs-order-type-bar flex items-center justify-between px-4 py-2 border-b bg-[rgba(16,24,40,0.03)]">
            {/* === END SAMPATH === */}
            <span className="text-[12px] font-medium text-[#667085] tracking-wide">
              Order Type
            </span>

            <span
              className="
                px-3 py-1
                text-[12px]
                font-semibold
                rounded-full
                bg-[rgba(16,24,40,0.08)]
                text-[#101828]
              "
            >
              {orderLable[checkoutData.orderType]}
            </span>
          </div>
          {/* === END SAMPATH === */}

          <div className="px-4">
            {userData || !!data?.clientId || !!gestData?.clientId ? (
              <div className="flex flex-col pt-3 px-2 md:px-[10%] gap-3">
                {/* === ADDED BY SAMPATH – 2026-01-15 === */}
                {/* Master details as LABELS (NOT text fields) for logged-in / guest verified users */}
                {preferredProfileData && (
                  <div className="dcs-master-card">
                    <div className="dcs-master-row">
                      <div className="dcs-master-label">Name</div>
                      <div className="dcs-master-value">
                        {`${dcsSafe(preferredProfileData?.firstName)} ${dcsSafe(
                          preferredProfileData?.lastName
                        )}`.trim()}
                      </div>
                    </div>

                    <div className="dcs-master-row">
                      <div className="dcs-master-label">Email</div>
                      <div className="dcs-master-value">
                        {dcsSafe(preferredProfileData?.email)}
                      </div>
                    </div>

                    <div className="dcs-master-row">
                      <div className="dcs-master-label">Contact Number</div>
                      <div className="dcs-master-value">
                        {dcsSafe(preferredProfileData?.phoneNo)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Old inputs kept (not removed) but hidden because you requested label view */}
                <div className="hidden">
                  <Input
                    size="small"
                    label="First Name"
                    sx={{ flex: 1 }}
                    value={data.firstName}
                  />
                  <Input
                    size="small"
                    label="Last Name"
                    sx={{ flex: 1 }}
                    value={data.lastName}
                  />
                  <Input
                    size="small"
                    label="Phone No."
                    sx={{ flex: 1 }}
                    value={data.phoneNo}
                  />
                  <Input
                    size="small"
                    label="Email"
                    sx={{ flex: 1 }}
                    value={data.email}
                  />
                </div>
                {/* === END SAMPATH === */}

                <Input
                  size="small"
                  label="Date"
                  type="date"
                  inputProps={{
                    min: format(new Date(), "yyyy-MM-dd"),
                    max: settings.AllowFutureDate
                      ? undefined
                      : format(new Date(), "yyyy-MM-dd"),
                  }}
                  sx={{ flex: 1 }}
                  value={data.onDate}
                  onValueChange={(onDate) => setData((e) => ({ ...e, onDate }))}
                />
                <Input
                  size="small"
                  label="Time"
                  type="time"
                  sx={{ flex: 1 }}
                  value={data.onTime}
                  onValueChange={(onTime) => {
                    if (data.onDate !== format(new Date(), "yyyy-MM-dd")) {
                      setData((prev) => ({
                        ...prev,
                        onTime,
                      }));
                      return;
                    }
                    const [currentHours, currentMinutes] = format(
                      new Date(),
                      "HH:mm"
                    )
                      .split(":")
                      .map(Number);
                    const [selectedHours, selectedMinutes] = onTime
                      .split(":")
                      .map(Number);

                    const currentTimeInMinutes =
                      currentHours * 60 + currentMinutes;
                    const selectedTimeInMinutes =
                      selectedHours * 60 + selectedMinutes;

                    setData((prev) => ({
                      ...prev,
                      onTime:
                        selectedTimeInMinutes > currentTimeInMinutes
                          ? onTime
                          : format(new Date(), "HH:mm"),
                    }));
                  }}
                />

                {checkoutData.orderType === ORDER.DELIVERY && (
                  <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/10 relative pt-4 px-4">
                    <p className="absolute left-[12px] -top-[10px] text-[12px] px-2 text-[#475467] bg-white rounded-full ring-1 ring-black/10">
                      Delivery Address
                    </p>

                    <RadioGroup
                      value={data.selectedAddress}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          selectedAddress: e.target.value,
                        }))
                      }
                      name="row-radio-buttons-group"
                    >
                      <div className="flex flex-col justify-center mb-2 ">
                        <FormControlLabel
                          value="home"
                          control={<Radio size="small" />}
                          label="Home Address"
                        />

                        <GoogleAddress
                          className="flex-1 ml-2"
                          value={data.address || ""}
                          onChange={(address) =>
                            setData((prev) => ({ ...prev, address }))
                          }
                        />
                      </div>

                      <div className="flex  flex-col justify-center mb-2">
                        <FormControlLabel
                          value="office"
                          control={<Radio size="small" />}
                          label="Office Address"
                        />

                        <GoogleAddress
                          className="flex-1 ml-2"
                          value={data.address1 || ""}
                          onChange={(address1) =>
                            setData((prev) => ({ ...prev, address1 }))
                          }
                        />
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-2 md:px-[10%]">
                {/* === ADDED BY SAMPATH – 2026-01-15 === */}
                {/* Login LEFT, Register + Guest RIGHT (modern header like your screenshot) */}
                <div className="dcs-auth-top dcs-auth-shell">
                  <div
                    className={`dcs-auth-left ${
                      tab === "Login" ? "dcs-auth-active" : ""
                    }`}
                    onClick={() => {
                      setTab("Login");
                      dispatch(setIsGuestUser(false));
                    }}
                  >
                    Login
                  </div>

                  <div className="dcs-auth-right">
                    <div
                      className={`dcs-auth-link ${
                        tab === "Register" ? "dcs-auth-active" : ""
                      }`}
                      onClick={() => {
                        setTab("Register");
                        dispatch(setIsGuestUser(false));
                      }}
                    >
                      Register
                    </div>

                    <div
                      className={`dcs-auth-link ${
                        tab === "Guest" ? "dcs-auth-active" : ""
                      }`}
                      onClick={() => {
                        setTab("Guest");
                        dispatch(setIsGuestUser(true));
                      }}
                    >
                      Guest
                    </div>
                  </div>
                </div>
                {/* === END SAMPATH === */}

                {/* === ADDED BY SAMPATH – 2026-01-15 === */}
                {/* Old 3-tab button bar kept (not removed) but hidden to match new UI */}
                <div className="hidden">
                  <div className="flex rounded-md mt-2">
                    <div
                      className={`flex-1 p-2 cursor-pointer text-center border border-primary transition-all duration-300 rounded-s-lg ${
                        tab === "Login" ? "bg-primary text-themeSelColor" : ""
                      }`}
                      onClick={() => {
                        setTab("Login");
                        dispatch(setIsGuestUser(false));
                      }}
                    >
                      Login
                    </div>
                    <div
                      className={`flex-1 p-2 text cursor-pointer text-center border border-primary transition-all duration-300 -mx-[0.3px] ${
                        tab === "Register" ? "bg-primary text-themeSelColor" : ""
                      }`}
                      onClick={() => {
                        setTab("Register");
                        dispatch(setIsGuestUser(false));
                      }}
                    >
                      Register
                    </div>
                    <div
                      className={`flex-1 p-2 cursor-pointer text-center border border-primary transition-all duration-300 rounded-e-lg ${
                        tab === "Guest" ? "bg-primary text-themeSelColor" : ""
                      }`}
                      onClick={() => {
                        setTab("Guest");
                        dispatch(setIsGuestUser(true));
                      }}
                    >
                      Guest
                    </div>
                  </div>
                </div>
                {/* === END SAMPATH === */}

                <div className="flex flex-col mt-3 gap-3">
                  {tab === "Login" && (
                    <>
                      <Input
                        size="small"
                        label="Email"
                        sx={{ flex: 1 }}
                        value={data.email}
                        onValueChange={(email) =>
                          setData((e) => ({ ...e, email }))
                        }
                      />
                      <Input
                        size="small"
                        label="Password"
                        type="password"
                        sx={{ flex: 1 }}
                        value={data.password}
                        onValueChange={(password) =>
                          setData((e) => ({ ...e, password }))
                        }
                      />
                    </>
                  )}

                  {tab === "Register" && (
                    <>
                      <div className="flex max-sm:flex-col gap-3">
                        <Input
                          size="small"
                          label="First Name"
                          value={data.firstName}
                          sx={{ flex: 1 }}
                          onValueChange={(firstName) =>
                            setData((e) => ({ ...e, firstName }))
                          }
                        />
                        <Input
                          size="small"
                          label="Last Name"
                          value={data.lastName}
                          sx={{ flex: 1 }}
                          onValueChange={(lastName) =>
                            setData((e) => ({ ...e, lastName }))
                          }
                        />
                      </div>
                      <div className="flex max-sm:flex-col gap-3">
                        <Input
                          size="small"
                          label="Phone Number"
                          value={data.phoneNo}
                          sx={{ flex: 1 }}
                          onValueChange={(phoneNo) =>
                            setData((e) => ({ ...e, phoneNo }))
                          }
                        />
                        <Input
                          size="small"
                          label="Email"
                          value={data.email}
                          sx={{ flex: 1 }}
                          onValueChange={(email) =>
                            setData((e) => ({ ...e, email }))
                          }
                        />
                      </div>
                      <div className="flex max-sm:flex-col gap-3">
                        <Input
                          size="small"
                          label="Password"
                          type="password"
                          value={data.password}
                          sx={{ flex: 1 }}
                          onValueChange={(password) =>
                            setData((e) => ({ ...e, password }))
                          }
                        />
                        <Input
                          size="small"
                          label="Confirm Password"
                          type="password"
                          value={data.confirmPassword}
                          sx={{ flex: 1 }}
                          onValueChange={(confirmPassword) =>
                            setData((e) => ({ ...e, confirmPassword }))
                          }
                        />
                      </div>

                      <GoogleAddress
                        className="flex-1"
                        lable="Home Address"
                        value={data.address}
                        onChange={(address) =>
                          setData((e) => ({ ...e, address }))
                        }
                      />
                      <GoogleAddress
                        className="col-span-2"
                        lable="Office Address"
                        value={data.address1}
                        onChange={(address1) =>
                          setData((e) => ({ ...e, address1 }))
                        }
                      />
                    </>
                  )}

                  {tab === "Guest" && (
                    <>
                      <div className="flex max-sm:flex-col gap-3">
                        <Input
                          size="small"
                          label="First Name"
                          value={data.firstName}
                          sx={{ flex: 1 }}
                          onValueChange={(firstName) =>
                            setData((e) => ({ ...e, firstName }))
                          }
                        />
                        <Input
                          size="small"
                          label="Last Name"
                          value={data.lastName}
                          sx={{ flex: 1 }}
                          onValueChange={(lastName) =>
                            setData((e) => ({ ...e, lastName }))
                          }
                        />
                      </div>
                      <div className="flex max-sm:flex-col gap-3">
                        <Input
                          size="small"
                          label="Phone Number"
                          value={data.phoneNo}
                          sx={{ flex: 1 }}
                          onValueChange={(phoneNo) =>
                            setData((e) => ({ ...e, phoneNo }))
                          }
                        />
                        <Input
                          size="small"
                          label="Email"
                          value={data.email}
                          sx={{ flex: 1 }}
                          onValueChange={(email) =>
                            setData((e) => ({ ...e, email }))
                          }
                        />
                      </div>

                      {checkoutData.orderType === ORDER.DELIVERY && (
                        <GoogleAddress
                          className="flex-1"
                          lable="Address"
                          value={data.address}
                          onChange={(address) =>
                            setData((e) => ({ ...e, address }))
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-b w-full mt-2 mb-1 flex-1" />
          {timeError ? (
            <div className="flex justify-center">
              <p className="max-w-[430px] text-center text-xs sm:text-sm text-red-500 mx-[5%]">
                {timeError}
              </p>
            </div>
          ) : (
            isRestDate && (
              <div className="flex justify-center">
                <p className="max-w-[430px] text-center text-xs sm:text-sm text-red-500 mx-[5%]">
                  Order placing is off please try again later
                </p>
              </div>
            )
          )}

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          <div className="dcs-continue-wrap">
            <Button
              className="dcs-continue-btn"
              onClick={handleSubmit}
              disabled={loading || !!timeError || isRestDate}
            >
              Continue
            </Button>
          </div>
          {/* === END SAMPATH === */}

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          <div className="h-3" />
          {/* === END SAMPATH === */}
        </div>
      </div>
    </>
  );
};

export default Details;
